import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RicercaComune.css';

/**
 * RicercaComune — si scrive e si sceglie dall'elenco ISTAT.
 *
 * Scrivere a mano produce lo stesso comune in tre grafie diverse, e
 * una provincia sbagliata rende incoerente il codice fiscale. Qui si
 * sceglie, e con il comune arrivano anche sigla, CAP e codice
 * catastale.
 *
 * Funziona sia dentro il gestionale sia sul tablet: se riceve la
 * chiave del dispositivo la usa al posto del token.
 *
 * Props:
 *  - valore      nome del comune
 *  - onChange    (nome) => void, mentre si scrive
 *  - onScelta    (comune) => void, alla scelta: { nome, sigla_provincia, cap, ... }
 *  - chiave      chiave del dispositivo, solo sul tablet
 *  - grande      comandi piu' larghi, per il tablet
 *  - errore, placeholder, id
 */
const RicercaComune = ({
    valore, onChange, onScelta, chiave, grande, errore, placeholder, id,
    // Passando il codice fiscale, il componente segnala se il comune
    // indicato non corrisponde a quello che risulta dal codice.
    codiceFiscale,
    // Se lo stato estero e' gia' indicato, l'avviso ha esaurito il
    // suo compito: chi compila sa gia' di essere nato fuori Italia.
    statoEstero,
}) => {
    const [risultati, setRisultati] = useState([]);
    const [incoerenza, setIncoerenza] = useState(null);
    const [aperto, setAperto] = useState(false);
    const [evidenziato, setEvidenziato] = useState(0);
    const contenitore = useRef(null);
    const scelto = useRef(false);

    useEffect(() => {
        const fuori = (e) => {
            if (contenitore.current && !contenitore.current.contains(e.target)) {
                setAperto(false);
            }
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, []);

    const cerca = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setRisultati([]);
            return;
        }
        try {
            const par = new URLSearchParams({ q: q.trim(), limit: '8' });
            if (chiave) par.set('chiave', chiave);

            const intestazioni = {};
            if (!chiave) {
                const token = localStorage.getItem('token');
                if (token) intestazioni.Authorization = `Bearer ${token}`;
            }

            const r = await fetch(`/api/comuni?${par}`, { headers: intestazioni });
            const d = await r.json();
            setRisultati(d.success ? (d.data.comuni || []) : []);
        } catch (e) {
            console.error('Ricerca comune:', e);
            setRisultati([]);
        }
    }, [chiave]);

    /* Attesa fra un tasto e l'altro: senza, ogni lettera e' una richiesta */
    useEffect(() => {
        if (scelto.current) { scelto.current = false; return; }
        const t = setTimeout(() => cerca(valore), 250);
        return () => clearTimeout(t);
    }, [valore, cerca]);

    /* Coerenza fra comune indicato e codice fiscale */
    useEffect(() => {
        const cf = String(codiceFiscale || '').replace(/\s/g, '');
        if (String(statoEstero || '').trim()) { setIncoerenza(null); return; }
        if (cf.length !== 16 || !valore || valore.trim().length < 3) {
            setIncoerenza(null);
            return;
        }
        let attivo = true;
        const t = setTimeout(async () => {
            try {
                const par = new URLSearchParams({ cf, comune: valore.trim() });
                if (chiave) par.set('chiave', chiave);

                const intestazioni = {};
                if (!chiave) {
                    const token = localStorage.getItem('token');
                    if (token) intestazioni.Authorization = `Bearer ${token}`;
                }

                const r = await fetch(`/api/comuni/verifica-cf?${par}`, { headers: intestazioni });
                const d = await r.json();
                if (!attivo) return;
                setIncoerenza(d.success && d.data.nota ? d.data.nota : null);
            } catch (e) {
                console.error('Verifica comune:', e);
            }
        }, 400);
        return () => { attivo = false; clearTimeout(t); };
    }, [codiceFiscale, valore, chiave, statoEstero]);

    const scegli = (c) => {
        scelto.current = true;
        onChange(c.nome);
        if (onScelta) onScelta(c);
        setAperto(false);
        setRisultati([]);
    };

    const tasti = (e) => {
        if (!aperto || !risultati.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setEvidenziato((i) => Math.min(i + 1, risultati.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEvidenziato((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (risultati[evidenziato]) scegli(risultati[evidenziato]);
        } else if (e.key === 'Escape') {
            setAperto(false);
        }
    };

    return (
        <div className={`rcom ${grande ? 'rcom--grande' : ''}`} ref={contenitore}>
            <input
                id={id}
                className={errore ? 'rcom-input errore' : 'rcom-input'}
                value={valore || ''}
                placeholder={placeholder || 'Scrivi le prime lettere…'}
                onChange={(e) => {
                    onChange(e.target.value);
                    setAperto(true);
                    setEvidenziato(0);
                }}
                onFocus={() => setAperto(true)}
                onKeyDown={tasti}
                autoComplete="off"
            />

            {incoerenza && !aperto && (
                <div className="rcom-incoerenza">{incoerenza}</div>
            )}

            {aperto && risultati.length > 0 && (
                <div className="rcom-lista">
                    {risultati.map((c, i) => (
                        <button
                            key={c.codice_catastale || c.nome}
                            type="button"
                            className={`rcom-voce ${i === evidenziato ? 'evidenziata' : ''}`}
                            onMouseEnter={() => setEvidenziato(i)}
                            onClick={() => scegli(c)}
                        >
                            <span className="rcom-nome">{c.nome}</span>
                            <span className="rcom-prov">
                                {c.sigla_provincia}
                                {c.cap ? ` · ${c.cap}` : ''}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RicercaComune;
